import { useState, useRef, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { consultationService } from '../services/consultationService'
import { patientFileService } from '../services/patientFileService'
import { patientService } from '../services/patientService'
import { stcService } from '../services/stcService'
import { pcrsService } from '../services/pcrsService'
import { gmsService } from '../services/gmsService'
import { tenantSettingsService } from '../services/tenantSettingsService'
import { useTenantProductType } from '../hooks/useTenantProductType'
import type { Consultation } from '../types/consultation'
import type { Gender as PatientGender, Patient, PatientFormData } from '../types/patient'
import type { StcFormData, StcFormType, StcRecord, StcTypeaheadSuggestion } from '../types/stc'
import { formatPatientName } from '../utils/name'
import { normalizeCodesList } from '../utils/consultationFields'
import { renderStcFormPng, type StcFormDataForPng } from '../utils/stcPng'
import { parseGmsNumberForLookup } from '../utils/pcrsGms'
import './StcSigning.css'

const SPECIAL_SERVICE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Please Select' },
  { value: 'A', label: 'A - Excisions/Cryotherapy/Diathermy of Skin Lesions' },
  { value: 'AB', label: "AB - LARC - counselling, insertion '&' monitoring from certified GP" },
  { value: 'AC', label: 'AC - Removal of a LARC Device ' },
  { value: 'AD', label: "AD - 24Hr ABPM Diagnosis '&' treatment for Individual patient care '&' not general/routine test" },
  { value: 'AL', label: 'AL - Therapeutic Phlebotomy for Haemochromatosis' },
  { value: 'B', label: 'B - Suturing Cuts & Lacerations-includes tissue glue does not include paper sutures' },
  { value: 'CE', label: 'CE - GP on sick leave due to Covid 19' },
  { value: 'CF', label: 'CF - Contraception Consultation for the purpose of accessing relevant products' },
  { value: 'CG', label: 'CG - LARC IMPLANT Fitting' },
  { value: 'CH', label: 'CH - LARC COIL Fitting' },
  { value: 'CI', label: 'CI - LARC IMPLANT Removal' },
  { value: 'CJ', label: 'CJ - LARC COIL Removal' },
  { value: 'CK', label: 'CK - Follow up consultation post LARC fitting' },
  { value: 'CL', label: 'CL - Contraception Consultation for the purpose of accessing relevant products Agreement 2023' },
  { value: 'CM', label: 'CM - LARC COIL Fitting Agreement 2023' },
  { value: 'CN', label: 'CN - LARC COIL Removal Agreement 2023' },
  { value: 'CO', label: 'CO - LARC IMPLANT Fitting Agreement 2023' },
  { value: 'CQ', label: 'CQ - LARC IMPLANT Removal Agreement 2023' },
  { value: 'D', label: 'D - Treatment and plugging of dental and nasal haemorrhages' },
  { value: 'F', label: "F - ECG tests '&' interpretation-individual patient care '&' not general/routine test" },
  { value: 'H', label: 'H - Removal of adherent foreign bodies from the surface of the Eye' },
  { value: 'J', label: 'J - Removal of lodged foreign bodies from the Ear, Nose and Throat' },
  { value: 'K', label: 'K - Nebuliser Treatment (in the case of Acute Asthmatic Attack)' },
  { value: 'L', label: 'L - Bladder catheterization – composite fee for insertion and removal' },
  { value: 'M', label: 'M - Attendance by GP at HSE convened case conference' },
  { value: 'X', label: 'X - Removal of lodged or impacted foreign bodies from the ear, nose, throat and skin' },
  { value: 'Y', label: 'Y - Suturing of cuts and lacerations (including application of tissue glue)' },
  { value: 'Z', label: 'Z - Draining of Abscess' },
]

const PRIVATE_SPECIAL_SERVICE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Please Select' },
  { value: 'CF', label: 'CF - Contraception Consultation for the purpose of accessing relevant products' },
  { value: 'CG', label: 'CG - LARC IMPLANT Fitting' },
  { value: 'CH', label: 'CH - LARC COIL Fitting' },
  { value: 'CI', label: 'CI - LARC IMPLANT Removal' },
  { value: 'CJ', label: 'CJ - LARC COIL Removal' },
  { value: 'CK', label: 'CK - Follow up consultation post LARC fitting' },
]

const STC_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Please Select' },
  { value: 'C', label: 'C - EC RESIDENT' },
  { value: 'E', label: 'E - EMERGENCY' },
  { value: 'H', label: 'H - OUT OF HOURS' },
  { value: 'O', label: 'O - SECOND MED. OPINION' },
  { value: 'T', label: 'T - TEMPORARY RESIDENT' },
]

const VISIT_CLASS_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Please Select' },
  { value: 'A', label: 'A - Additional Fee' },
  { value: 'D', label: 'D - Normal Hours' },
  { value: 'L', label: 'L - Outside normal hours other than N' },
  { value: 'N', label: 'N - Midnight to 8 a.m.' },
]

const CLAIM_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Please Select' },
  { value: 'A', label: 'A - Capitation' },
  { value: 'D', label: 'D - FPI Surgery' },
  { value: 'E', label: 'E - FPI Domiciliary' },
]

const LOCATION_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Please Select' },
  { value: 'D', label: 'D - Domiciliary' },
  { value: 'S', label: 'S - Surgery' },
]

const DISTANCE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Please Select' },
  { value: 'A', label: 'A - up to 3 miles' },
  { value: 'B', label: 'B - 3 - 5 miles' },
  { value: 'C', label: 'C - 5 - 7 miles' },
  { value: 'D', label: 'D - 7 - 10 miles' },
  { value: 'E', label: 'E - over 10 miles' },
]

const EHIC_COUNTRY_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Please Select' },
  { value: 'AT', label: 'AT - AUSTRIA' },
  { value: 'BE', label: 'BE - BELGIUM' },
  { value: 'CY', label: 'CY - CYPRUS' },
  { value: 'CZ', label: 'CZ - CZECH REPUBLIC' },
  { value: 'DK', label: 'DK - DENMARK' },
  { value: 'EE', label: 'EE - ESTONIA' },
  { value: 'FI', label: 'FI - FINLAND' },
  { value: 'FR', label: 'FR - FRANCE' },
  { value: 'DE', label: 'DE - GERMANY' },
  { value: 'GR', label: 'GR - GREECE' },
  { value: 'HU', label: 'HU - HUNGARY' },
  { value: 'IT', label: 'IT - ITALY' },
  { value: 'LV', label: 'LV - LATVIA' },
  { value: 'LT', label: 'LT - LITHUANIA' },
  { value: 'LU', label: 'LU - LUXEMBOURG' },
  { value: 'MT', label: 'MT - MALTA' },
  { value: 'NL', label: 'NL - NETHERLANDS' },
  { value: 'PL', label: 'PL - POLAND' },
  { value: 'PT', label: 'PT - PORTUGAL' },
  { value: 'SK', label: 'SK - SLOVAKIA' },
  { value: 'SI', label: 'SI - SLOVENIA' },
  { value: 'ES', label: 'ES - SPAIN' },
  { value: 'SE', label: 'SE - SWEDEN' },
  { value: 'UK', label: 'UK - UNITED KINGDOM' },
  { value: 'IS', label: 'IS - ICELAND' },
  { value: 'LI', label: 'LI - LIECHTENSTEIN' },
  { value: 'NO', label: 'NO - NORWAY' },
  { value: 'CH', label: 'CH - SWITZERLAND' },
  { value: 'RO', label: 'RO - ROMANIA' },
  { value: 'BG', label: 'BG - BULGARIA' },
  { value: 'HR', label: 'HR - CROATIA' },
]

const RELATIONSHIP_OPTIONS = [
  '',
  'Parent / Guardian',
  'Spouse / Partner',
  'Adult Child',
  'Sibling',
  'Other Relative',
  'Carer / Support Person',
  'Nursing Home Representative',
  'Social Worker / HSE Representative',
  'Healthcare Professional (e.g., GP/Nurse)',
  'Legal Guardian (court-appointed)',
  'Enduring Power of Attorney (EPA) – Attorney',
  'Decision-Making Representative (DMR)',
  'Solicitor / Legal Representative',
  'Interpreter / Translator',
  'Friend / Neighbour',
  'Other',
]
const TYPEAHEAD_MIN_CHARS = 3
const TYPEAHEAD_DEBOUNCE_MS = 300

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function nowLocalDate() {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function nowLocalDatetime(): string {
  const now = new Date()
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}T${pad2(now.getHours())}:${pad2(now.getMinutes())}`
}

function normalizePersonName(value: string): string {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
}

function isEligibilityExpired(eligibilityEndDate?: string): boolean {
  const raw = String(eligibilityEndDate || '').trim()
  if (!raw) return false
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return d < today
}

function formatDateToDmy(value?: string): string {
  const raw = String(value || '').trim()
  if (!raw) return ''
  // Preserve source date portion when timestamp includes timezone to avoid day-shift display.
  const withTime = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/)
  if (withTime) return `${withTime[3]}/${withTime[2]}/${withTime[1]}`
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const year = parsed.getFullYear()
  return `${day}/${month}/${year}`
}

function normalizeDateInput(value?: string): string {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/)
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`
  const dmy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return ''
  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const year = parsed.getFullYear()
  return `${year}-${month}-${day}`
}

function normalizeStcGenderCode(value?: string): 'F' | 'M' | 'O' | 'U' | '' {
  const raw = String(value || '').trim().toUpperCase()
  if (!raw) return ''
  if (raw === 'FEMALE') return 'F'
  if (raw === 'MALE') return 'M'
  if (raw === 'OTHER') return 'O'
  if (raw === 'UNKNOWN') return 'U'
  if (raw === 'F' || raw === 'M' || raw === 'O' || raw === 'U') return raw
  return ''
}

function mapPatientGenderToStcCode(value?: PatientGender): 'F' | 'M' | 'O' | 'U' | '' {
  if (value === 'female') return 'F'
  if (value === 'male') return 'M'
  if (value === 'other') return 'O'
  if (value === 'unknown') return 'U'
  return ''
}

function mapStcCodeToPatientGender(value?: string): PatientGender | undefined {
  const code = normalizeStcGenderCode(value)
  if (code === 'F') return 'female'
  if (code === 'M') return 'male'
  if (code === 'O') return 'other'
  if (code === 'U') return 'unknown'
  return undefined
}

type GmsEligibilityUiState =
  | { status: 'idle'; text: '' }
  | { status: 'checking'; text: string }
  | { status: 'valid'; text: string; expiry?: string }
  | { status: 'expired'; text: string; expiry?: string }
  | { status: 'invalid'; text: string; pcrsName?: string }
  | { status: 'error'; text: string }

type StcClaimMode = 'gms' | 'private'
type PpsLookupUiState =
  | { status: 'idle'; text: '' }
  | { status: 'checking'; text: string }
  | { status: 'valid'; text: string; details?: string }
  | { status: 'not_found'; text: string }
  | { status: 'invalid'; text: string }
  | { status: 'error'; text: string }

export interface StcSigningEmbeddedProps {
  embedded?: boolean
  consultationIdProp?: string
  recordIdProp?: string
  patientIdProp?: string
  patientNameProp?: string
  formTypeProp?: StcFormType
  readOnly?: boolean
  /** Default location when opening from STC badge (e.g. 'S' for Surgery) */
  initialLocationProp?: string
  /** Special Service code pre-filled from STC detection (e.g. 'K' for Nebuliser) */
  initialSpecialServiceProp?: string
  /** When opening from badge, pass appointmentId so saving the STC clears the badge and sets stcRaised */
  appointmentIdProp?: string
  onClose?: () => void
  onSuccess?: () => void
}

export function StcSigning(props: StcSigningEmbeddedProps = {}) {
  const {
    embedded = false,
    consultationIdProp,
    recordIdProp,
    patientIdProp,
    patientNameProp,
    formTypeProp,
    readOnly = false,
    initialLocationProp,
    initialSpecialServiceProp,
    appointmentIdProp,
    onClose,
    onSuccess,
  } = props
  const { id: consultationIdFromParams } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const specialServiceSelectRef = useRef<HTMLSelectElement>(null)
  const ppsInputRef = useRef<HTMLInputElement>(null)
  const patientNameInputRef = useRef<HTMLInputElement>(null)
  const motherNameInputRef = useRef<HTMLInputElement>(null)
  const { isPptOnly } = useTenantProductType()

  const consultationId = embedded ? consultationIdProp ?? undefined : consultationIdFromParams
  const isStandalonePptMode = isPptOnly && !consultationId

  const { data: consultation, isLoading: loadingConsultation, error: consultationError } = useQuery<Consultation>({
    queryKey: ['consultation', consultationId],
    queryFn: () => consultationService.getById(consultationId!),
    enabled: !!consultationId,
  })

  const { data: patient, isLoading: loadingPatient, error: patientError } = useQuery<Patient>({
    queryKey: ['patient', consultation?.patientId],
    queryFn: () => patientService.getById(consultation!.patientId),
    enabled: !!consultation?.patientId,
  })

  const { data: tenantSettings } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => tenantSettingsService.getCurrent(),
  })

  const { data: existingRecord } = useQuery<StcRecord>({
    queryKey: ['stc-record', recordIdProp],
    queryFn: () => stcService.getById(recordIdProp!),
    enabled: !!recordIdProp && !!embedded,
  })

  const [formType, setFormType] = useState<StcFormType>(formTypeProp ?? 'STC')
  const [stcType, setStcType] = useState('0')
  const [claimType, setClaimType] = useState('A')
  const [location, setLocation] = useState('S')
  const [distance, setDistance] = useState('0')
  const [visitClass, setVisitClass] = useState('D')
  const [ehicNoPrescriptionRequired, setEhicNoPrescriptionRequired] = useState(false)
  const [ehicPrescriptionSerialNumber, setEhicPrescriptionSerialNumber] = useState('')
  const [ehicCountryCde, setEhicCountryCde] = useState('0')
  const [ehicPatientForeName, setEhicPatientForeName] = useState('')
  const [ehicPatientSurname, setEhicPatientSurname] = useState('')
  const [ehicDob, setEhicDob] = useState('')
  const [ehicNumber, setEhicNumber] = useState('')
  const [ehicPersonalIdNumber, setEhicPersonalIdNumber] = useState('')
  const [ehicInstitutionNumber, setEhicInstitutionNumber] = useState('')
  const [ehicPatientExpiryDate, setEhicPatientExpiryDate] = useState('')
  const [patientName, setPatientName] = useState('')
  const [gmsNumber, setGmsNumber] = useState('')
  const [ppsn, setPpsn] = useState('')
  const [privateDob, setPrivateDob] = useState('')
  const [privateGender, setPrivateGender] = useState<'F' | 'M' | 'O' | 'U'>('F')
  const [claimMode, setClaimMode] = useState<StcClaimMode>('gms')
  const [date, setDate] = useState('')
  const [specialService, setSpecialService] = useState('0')
  const [oohPatientConfirmed, setOohPatientConfirmed] = useState(false)
  const [oohSignaturePresent, setOohSignaturePresent] = useState(false)
  const [patientSignedOnPaper, setPatientSignedOnPaper] = useState(false)
  const [oohGpConfirmed, setOohGpConfirmed] = useState(false)
  const [oohCoopRota, setOohCoopRota] = useState(false)
  const [repName, setRepName] = useState('')
  const [motherName, setMotherName] = useState('')
  const [micsClaimDate, setMicsClaimDate] = useState('')
  const [micsEDD, setMicsEDD] = useState('')
  const [micsService, setMicsService] = useState('')
  const [sickCertIcd10PrimaryCode, setSickCertIcd10PrimaryCode] = useState('')
  const [sickCertIcd10Description, setSickCertIcd10Description] = useState('')
  const [isLookupLoading, setIsLookupLoading] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [storedSignatureDataUrl, setStoredSignatureDataUrl] = useState<string | null>(null)
  const [isStoredSignatureLoading, setIsStoredSignatureLoading] = useState(false)
  const [storedSignatureError, setStoredSignatureError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [saveWarning, setSaveWarning] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [wacomCapturing, setWacomCapturing] = useState(false)
  const [wacomScriptLoaded, setWacomScriptLoaded] = useState(false)
  const [activeSuggestionId, setActiveSuggestionId] = useState<string | null>(null)
  const [gmsEligibilityUi, setGmsEligibilityUi] = useState<GmsEligibilityUiState>({ status: 'idle', text: '' })
  const [ppsLookupUi, setPpsLookupUi] = useState<PpsLookupUiState>({ status: 'idle', text: '' })
  const lastEligibilityLookupKeyRef = useRef<string>('')
  const lastPpsLookupValueRef = useRef<string>('')
  const usePcrsNameButtonPressedRef = useRef(false)
  const saveInFlightRef = useRef(false)
  const [showPatientNameSuggestions, setShowPatientNameSuggestions] = useState(false)
  const [allowNameTypeahead, setAllowNameTypeahead] = useState(true)
  const [showGmsSuggestions, setShowGmsSuggestions] = useState(false)
  const [showMicsNameSuggestions, setShowMicsNameSuggestions] = useState(false)
  const [activePatientSuggestionIndex, setActivePatientSuggestionIndex] = useState(-1)
  const [activeGmsSuggestionIndex, setActiveGmsSuggestionIndex] = useState(-1)
  const [activeMicsNameSuggestionIndex, setActiveMicsNameSuggestionIndex] = useState(-1)
  const useStoredSignatureView = embedded && !!recordIdProp
  const isStatusReadOnly =
    existingRecord?.status === 'to_pay' ||
    existingRecord?.status === 'paid' ||
    existingRecord?.status === 'rejected' ||
    existingRecord?.status === 'failed'
  const isReadOnlyMode = readOnly || isStatusReadOnly
  const settingsWacomLicenceId = tenantSettings?.stcWacomLicenceId ?? null
  const settingsWacomLicenceKey = tenantSettings?.stcWacomLicenceKey ?? null
  const sessionWacomLicenceId =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('clnicl_stc_wacom_licence_id') : null
  const sessionWacomLicenceKey =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('clnicl_stc_wacom_licence_key') : null

  const formatTypeaheadDob = useCallback((value?: string) => {
    return formatDateToDmy(value)
  }, [])

  const patientNameDisplay = patient ? formatPatientName(patient) : (embedded && patientNameProp ? patientNameProp : '')
  const wacomAvailable = !!(
    (settingsWacomLicenceId || sessionWacomLicenceId) &&
    (settingsWacomLicenceKey || sessionWacomLicenceKey)
  )
  const isOutOfHoursVisit = visitClass === 'N' || visitClass === 'L'
  const isEhicResident = formType === 'STC' && stcType === 'C'
  const isEmergencyStcType = formType === 'STC' && stcType === 'E'
  const isPrivateClaim = formType === 'STC' && claimMode === 'private'
  const availableSpecialServiceOptions = isPrivateClaim ? PRIVATE_SPECIAL_SERVICE_OPTIONS : SPECIAL_SERVICE_OPTIONS
  const specialServiceForPayload = isEmergencyStcType ? '0' : specialService

  const patientNameQueryInput = isStandalonePptMode ? patientName.trim() : ''
  const motherNameQueryInput = isStandalonePptMode ? motherName.trim() : ''
  const gmsQueryInput = isStandalonePptMode ? gmsNumber.trim() : ''
  const patientNameQuery = useDebouncedValue(patientNameQueryInput, TYPEAHEAD_DEBOUNCE_MS)
  const motherNameQuery = useDebouncedValue(motherNameQueryInput, TYPEAHEAD_DEBOUNCE_MS)
  const gmsQuery = useDebouncedValue(gmsQueryInput, TYPEAHEAD_DEBOUNCE_MS)

  const { data: patientNameSuggestions = [], isFetching: isPatientNameSearching } = useQuery<StcTypeaheadSuggestion[]>({
    queryKey: ['ppt-typeahead', 'patientName', patientNameQuery],
    queryFn: () => stcService.getTypeahead('patientName', patientNameQuery, 8),
    enabled: isStandalonePptMode && allowNameTypeahead && patientNameQuery.length >= TYPEAHEAD_MIN_CHARS,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })

  const { data: gmsSuggestions = [] } = useQuery<StcTypeaheadSuggestion[]>({
    queryKey: ['ppt-typeahead', 'gmsNumber', gmsQuery],
    queryFn: () => stcService.getTypeahead('gmsNumber', gmsQuery, 8),
    enabled: isStandalonePptMode && gmsQuery.length >= TYPEAHEAD_MIN_CHARS,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })

  const { data: micsNameSuggestions = [], isFetching: isMicsNameSearching } = useQuery<StcTypeaheadSuggestion[]>({
    queryKey: ['ppt-typeahead', 'motherName', motherNameQuery],
    queryFn: () => stcService.getTypeahead('patientName', motherNameQuery, 8),
    enabled: isStandalonePptMode && motherNameQuery.length >= TYPEAHEAD_MIN_CHARS,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (patientNameDisplay) setPatientName(patientNameDisplay)
  }, [patientNameDisplay])

  useEffect(() => {
    if (patient?.gmsNumber) setGmsNumber(patient.gmsNumber)
  }, [patient?.gmsNumber])

  useEffect(() => {
    if (patient?.ppsNumber) setPpsn(String(patient.ppsNumber).trim())
  }, [patient?.ppsNumber])

  useEffect(() => {
    if (patient?.dateOfBirth) setPrivateDob(normalizeDateInput(patient.dateOfBirth))
    const mappedGender = mapPatientGenderToStcCode(patient?.gender)
    if (mappedGender) setPrivateGender(mappedGender)
  }, [patient?.dateOfBirth, patient?.gender])

  useEffect(() => {
    if (stcType === 'H') setVisitClass('N')
  }, [stcType])

  useEffect(() => {
    if (stcType === '0') {
      setVisitClass('D')
      setDate(nowLocalDatetime())
    }
  }, [stcType])

  useEffect(() => {
    if (formType !== 'STC') return
    setGmsEligibilityUi({ status: 'idle', text: '' })
    setPpsLookupUi({ status: 'idle', text: '' })
    setFormError(null)
    setSaveWarning(null)
  }, [claimMode, formType])

  useEffect(() => {
    if (formType !== 'STC' || claimMode !== 'private') return
    const timer = window.setTimeout(() => ppsInputRef.current?.focus(), 0)
    return () => window.clearTimeout(timer)
  }, [claimMode, formType])

  useEffect(() => {
    const handleClaimModeHotkey = (event: KeyboardEvent) => {
      if (formType !== 'STC' || !event.ctrlKey) return
      const key = String(event.key || '').toLowerCase()
      if (key === 'g') {
        event.preventDefault()
        setClaimMode('gms')
      } else if (key === 'p') {
        event.preventDefault()
        setClaimMode('private')
      }
    }
    window.addEventListener('keydown', handleClaimModeHotkey)
    return () => window.removeEventListener('keydown', handleClaimModeHotkey)
  }, [formType])

  useEffect(() => {
    if (!isPrivateClaim) return
    if (!availableSpecialServiceOptions.some((opt) => opt.value === specialService)) {
      setSpecialService('0')
    }
  }, [availableSpecialServiceOptions, isPrivateClaim, specialService])

  useEffect(() => {
    if (!useStoredSignatureView || !recordIdProp || !existingRecord?.signatureS3Key) {
      setStoredSignatureDataUrl(null)
      setStoredSignatureError(null)
      setIsStoredSignatureLoading(false)
      return
    }
    let cancelled = false
    setIsStoredSignatureLoading(true)
    setStoredSignatureError(null)
    ;(async () => {
      try {
        const dataUrl = await stcService.getSignatureDataUrl(recordIdProp)
        if (!cancelled) setStoredSignatureDataUrl(dataUrl)
      } catch {
        if (!cancelled) {
          setStoredSignatureDataUrl(null)
          setStoredSignatureError('Unable to load previously captured signature.')
        }
      } finally {
        if (!cancelled) setIsStoredSignatureLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [existingRecord?.signatureS3Key, recordIdProp, useStoredSignatureView])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (formType === 'MICS') {
        motherNameInputRef.current?.focus()
      } else {
        patientNameInputRef.current?.focus()
      }
    }, 0)
    return () => window.clearTimeout(timer)
  }, [formType])

  useEffect(() => {
    setActivePatientSuggestionIndex(patientNameSuggestions.length > 0 ? 0 : -1)
  }, [patientNameSuggestions])

  useEffect(() => {
    setActiveGmsSuggestionIndex(gmsSuggestions.length > 0 ? 0 : -1)
  }, [gmsSuggestions])

  useEffect(() => {
    setActiveMicsNameSuggestionIndex(micsNameSuggestions.length > 0 ? 0 : -1)
  }, [micsNameSuggestions])

  useEffect(() => {
    if (consultation && !date) {
      setDate(isOutOfHoursVisit ? nowLocalDate() : nowLocalDatetime())
    }
  }, [consultation, isOutOfHoursVisit, date])

  useEffect(() => {
    if (!consultation?.id || formType !== 'STC') return
    const codes = normalizeCodesList(consultation.codes)
    const firstIcd = codes.find((c) => c.system === 'ICD10')
    if (!firstIcd) return
    setSickCertIcd10PrimaryCode((prev) => prev.trim() || firstIcd.code)
    setSickCertIcd10Description((prev) => prev.trim() || firstIcd.display)
  }, [consultation?.id, consultation?.codes, formType])

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return
    if (settingsWacomLicenceId) {
      sessionStorage.setItem('clnicl_stc_wacom_licence_id', settingsWacomLicenceId)
    }
    if (settingsWacomLicenceKey) {
      sessionStorage.setItem('clnicl_stc_wacom_licence_key', settingsWacomLicenceKey)
    }
  }, [settingsWacomLicenceId, settingsWacomLicenceKey])

  useEffect(() => {
    if (isOutOfHoursVisit && date && date.includes('T')) {
      setDate(date.slice(0, 10))
    } else if (!isOutOfHoursVisit && date && !date.includes('T')) {
      setDate(nowLocalDatetime())
    }
  }, [isOutOfHoursVisit])

  // Pre-fill form from existing record when embedded
  useEffect(() => {
    if (!embedded || !existingRecord?.formData) return
    const fd = existingRecord.formData
    if (fd.stcType !== undefined) setStcType(fd.stcType)
    if (fd.claimType !== undefined) setClaimType(fd.claimType)
    if (fd.location !== undefined) setLocation(fd.location)
    if (fd.distance !== undefined) setDistance(fd.distance)
    if (fd.visitClass !== undefined) setVisitClass(fd.visitClass)
    if (fd.ehicNoPrescriptionRequired !== undefined) setEhicNoPrescriptionRequired(fd.ehicNoPrescriptionRequired)
    if (fd.ehicPrescriptionSerialNumber !== undefined) setEhicPrescriptionSerialNumber(fd.ehicPrescriptionSerialNumber)
    if (fd.ehicCountryCde !== undefined) setEhicCountryCde(fd.ehicCountryCde)
    if (fd.ehicPatientForeName !== undefined) setEhicPatientForeName(fd.ehicPatientForeName)
    if (fd.ehicPatientSurname !== undefined) setEhicPatientSurname(fd.ehicPatientSurname)
    if (fd.ehicDob !== undefined) setEhicDob(fd.ehicDob)
    if (fd.ehicNumber !== undefined) setEhicNumber(fd.ehicNumber)
    if (fd.ehicPersonalIdNumber !== undefined) setEhicPersonalIdNumber(fd.ehicPersonalIdNumber)
    if (fd.ehicInstitutionNumber !== undefined) setEhicInstitutionNumber(fd.ehicInstitutionNumber)
    if (fd.ehicPatientExpiryDate !== undefined) setEhicPatientExpiryDate(fd.ehicPatientExpiryDate)
    if (fd.patientName !== undefined) setPatientName(fd.patientName)
    if (fd.claimMode === 'gms' || fd.claimMode === 'private') {
      setClaimMode(fd.claimMode)
    } else if (fd.ppsn && !fd.gmsNumber) {
      setClaimMode('private')
    } else if (fd.gmsNumber) {
      setClaimMode('gms')
    }
    if (fd.gmsNumber !== undefined) setGmsNumber(fd.gmsNumber)
    if (fd.ppsn !== undefined) setPpsn(fd.ppsn)
    if (fd.dateOfBirth !== undefined) setPrivateDob(normalizeDateInput(fd.dateOfBirth))
    if (fd.gender !== undefined) {
      const normalizedGender = normalizeStcGenderCode(fd.gender)
      if (normalizedGender) setPrivateGender(normalizedGender)
    }
    if (fd.date !== undefined) setDate(fd.date)
    if (fd.specialService !== undefined) setSpecialService(fd.specialService)
    if (fd.oohPatientConfirmed !== undefined) setOohPatientConfirmed(fd.oohPatientConfirmed)
    if (fd.oohSignaturePresent !== undefined) setOohSignaturePresent(fd.oohSignaturePresent)
    if (fd.patientSignedOnPaper !== undefined) setPatientSignedOnPaper(Boolean(fd.patientSignedOnPaper))
    if (fd.oohGpConfirmed !== undefined) setOohGpConfirmed(fd.oohGpConfirmed)
    if (fd.oohCoopRota !== undefined) setOohCoopRota(fd.oohCoopRota)
    if (fd.repName !== undefined) setRepName(fd.repName)
    if (fd.motherName !== undefined) setMotherName(fd.motherName)
    if (fd.micsClaimDate !== undefined) setMicsClaimDate(fd.micsClaimDate)
    if (fd.micsEDD !== undefined) setMicsEDD(fd.micsEDD)
    if (fd.micsService !== undefined) setMicsService(fd.micsService)
    if (fd.sickCertIcd10PrimaryCode !== undefined) setSickCertIcd10PrimaryCode(fd.sickCertIcd10PrimaryCode)
    if (fd.sickCertIcd10Description !== undefined) setSickCertIcd10Description(fd.sickCertIcd10Description)
  }, [embedded, existingRecord?.formData])

  // Default location (Surgery) and Special Service from STC detection when opening from badge (no existing record)
  useEffect(() => {
    if (!embedded || recordIdProp || (!initialLocationProp && !initialSpecialServiceProp)) return
    if (initialLocationProp && initialLocationProp !== '0') setLocation(initialLocationProp)
    if (initialSpecialServiceProp && initialSpecialServiceProp !== '0') setSpecialService(initialSpecialServiceProp)
  }, [embedded, recordIdProp, initialLocationProp, initialSpecialServiceProp])

  // Initial formType from record when embedded
  useEffect(() => {
    if (embedded && existingRecord?.formType) setFormType(existingRecord.formType)
  }, [embedded, existingRecord?.formType])

  const declarationText =
    formType === 'MICS'
      ? 'I confirm attendance for the Mother & Infant Care Scheme consultation as outlined.'
      : 'I confirm that I attended the consultation as outlined above.'

  const clearForm = useCallback(() => {
    setFormType('STC')
    setStcType('0')
    setClaimType('A')
    setLocation('S')
    setDistance('0')
    setVisitClass('D')
    setEhicNoPrescriptionRequired(false)
    setEhicPrescriptionSerialNumber('')
    setEhicCountryCde('0')
    setEhicPatientForeName('')
    setEhicPatientSurname('')
    setEhicDob('')
    setEhicNumber('')
    setEhicPersonalIdNumber('')
    setEhicInstitutionNumber('')
    setEhicPatientExpiryDate('')
    setPatientName(patientNameDisplay)
    setGmsNumber(patient?.gmsNumber ?? '')
    setPpsn(String(patient?.ppsNumber || '').trim())
    setPrivateDob(normalizeDateInput(patient?.dateOfBirth))
    setPrivateGender(mapPatientGenderToStcCode(patient?.gender) || 'F')
    setClaimMode('gms')
    setGmsEligibilityUi({ status: 'idle', text: '' })
    setPpsLookupUi({ status: 'idle', text: '' })
    lastEligibilityLookupKeyRef.current = ''
    lastPpsLookupValueRef.current = ''
    setDate(nowLocalDatetime())
    setSpecialService('0')
    setOohPatientConfirmed(false)
    setOohSignaturePresent(false)
    setPatientSignedOnPaper(false)
    setOohGpConfirmed(false)
    setOohCoopRota(false)
    setRepName('')
    setMotherName('')
    setMicsClaimDate('')
    setMicsEDD('')
    setMicsService('')
    setSickCertIcd10PrimaryCode('')
    setSickCertIcd10Description('')
    setFormError(null)
    setSaveWarning(null)
    setSaveSuccess(false)
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    window.setTimeout(() => {
      if (formType === 'MICS') {
        motherNameInputRef.current?.focus()
      } else {
        patientNameInputRef.current?.focus()
      }
    }, 0)
  }, [formType, patient?.dateOfBirth, patient?.gender, patient?.gmsNumber, patient?.ppsNumber, patientNameDisplay])

  const clearSignature = useCallback(() => {
    if (useStoredSignatureView) return
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
  }, [useStoredSignatureView])

  const getSignatureDataUrl = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    const w = canvas.width
    const h = canvas.height
    const hasContent = ctx.getImageData(0, 0, w, h).data.some((v, i) => i % 4 === 3 && v > 0)
    return hasContent ? canvas.toDataURL('image/png') : null
  }, [])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (useStoredSignatureView) return
      e.preventDefault()
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height
      const x = (e.clientX - rect.left) * scaleX
      const y = (e.clientY - rect.top) * scaleY
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.strokeStyle = '#1f2937'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      setIsDrawing(true)
    },
    [useStoredSignatureView]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      if (!isDrawing) return
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height
      const x = (e.clientX - rect.left) * scaleX
      const y = (e.clientY - rect.top) * scaleY
      ctx.lineTo(x, y)
      ctx.stroke()
    },
    [isDrawing]
  )

  const handlePointerUp = useCallback(() => {
    setIsDrawing(false)
  }, [])

  const loadWacomScript = useCallback((): Promise<void> => {
    if (typeof window === 'undefined') return Promise.reject(new Error('Not in browser'))
    if (wacomScriptLoaded) return Promise.resolve()
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-wacom-init="true"]')
      if (existing) {
        setWacomScriptLoaded(true)
        resolve()
        return
      }
      const script = document.createElement('script')
      script.type = 'module'
      script.src = `${import.meta.env.BASE_URL || '/'}wacom/wacom_init.js`
      script.dataset.wacomInit = 'true'
      script.onload = () => {
        setWacomScriptLoaded(true)
        resolve()
      }
      script.onerror = () => reject(new Error('Failed to load Wacom signature script.'))
      document.head.appendChild(script)
    })
  }, [wacomScriptLoaded])

  const handleWacomSign = useCallback(async () => {
    setFormError(null)
    const licenceId = settingsWacomLicenceId || sessionWacomLicenceId
    const licenceKey = settingsWacomLicenceKey || sessionWacomLicenceKey
    if (!licenceId || !licenceKey) {
      setFormError('Wacom licence not available. Enter and save the Wacom licence ID and key in Settings → STC & PCRS.')
      return
    }
    if (typeof window === 'undefined') return
    ;(window as unknown as { __STC_WACOM_LICENCE__?: { licenceId: string; licenceKey: string } }).__STC_WACOM_LICENCE__ = {
      licenceId,
      licenceKey,
    }
    setWacomCapturing(true)
    try {
      await loadWacomScript()
      const captureFromSTU = (window as unknown as { captureFromSTU?: () => Promise<string> }).captureFromSTU
      if (!captureFromSTU) {
        setFormError('Wacom capture not available after loading script.')
        return
      }
      const dataUrl = await captureFromSTU()
      const canvas = canvasRef.current
      if (canvas && dataUrl) {
        const ctx = canvas.getContext('2d')
        if (ctx) {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              ctx.clearRect(0, 0, canvas.width, canvas.height)
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
              resolve()
            }
            img.onerror = () => reject(new Error('Failed to load signature image.'))
            img.src = dataUrl
          })
        }
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Wacom capture failed.')
    } finally {
      setWacomCapturing(false)
    }
  }, [
    settingsWacomLicenceId,
    settingsWacomLicenceKey,
    sessionWacomLicenceId,
    sessionWacomLicenceKey,
    loadWacomScript,
  ])

  const buildPaperSignatureDataUrl = useCallback(() => {
    const blank = document.createElement('canvas')
    blank.width = 600
    blank.height = 170
    const ctx = blank.getContext('2d')
    if (ctx) {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, blank.width, blank.height)
      ctx.fillStyle = '#6b7280'
      ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto'
      ctx.fillText('Signed on paper', 16, 30)
    }
    return blank.toDataURL('image/png')
  }, [])

  const handleSave = useCallback(async () => {
    setFormError(null)
    setSaveWarning(null)
    setSaveSuccess(false)
    if (formType === 'STC') {
      if (!patientName.trim()) {
        setFormError('Patient name is required.')
        return
      }
      if (!date.trim()) {
        setFormError('Date & time of consultation is required.')
        return
      }
      if (!isEmergencyStcType && (!specialService || specialService === '0')) {
        setFormError('Special Service is required.')
        return
      }
      if (claimMode === 'private' && !ppsn.trim()) {
        setFormError('PPS number is required for Private Patient claims.')
        return
      }
      if (claimMode === 'private' && ppsLookupUi.status === 'not_found' && !privateDob.trim()) {
        setFormError('Date of birth is required when PPS status is Not Found.')
        return
      }
      if (isEhicResident) {
        if (!ehicNoPrescriptionRequired && !ehicPrescriptionSerialNumber.trim()) {
          setFormError('Prescription serial number is required for EC Resident unless no prescription is required.')
          return
        }
        if (!ehicCountryCde || ehicCountryCde === '0') {
          setFormError('State identifier is required for EC Resident.')
          return
        }
        if (!ehicPatientForeName.trim() || !ehicPatientSurname.trim()) {
          setFormError('Given name and surname are required for EC Resident.')
          return
        }
        if (!ehicDob.trim()) {
          setFormError('Date of birth is required for EC Resident.')
          return
        }
        if (!ehicNumber.trim()) {
          setFormError('Identification number is required for EC Resident.')
          return
        }
        if (!ehicPersonalIdNumber.trim()) {
          setFormError('Personal ID number is required for EC Resident.')
          return
        }
        if (!ehicInstitutionNumber.trim()) {
          setFormError('Identification number of the institution is required for EC Resident.')
          return
        }
        if (!ehicPatientExpiryDate.trim()) {
          setFormError('Card expiry date is required for EC Resident.')
          return
        }
      }
      if (isOutOfHoursVisit) {
        if (!oohPatientConfirmed) {
          setFormError('Please confirm the patient has confirmed Non-Routine Out-of-Hours consultation.')
          return
        }
        if (!oohSignaturePresent) {
          setFormError('Please confirm the patient signature (or representative) is present.')
          return
        }
        if (!oohGpConfirmed) {
          setFormError('Please confirm the claiming GP has confirmed Non-Routine Out-of-Hours consultation.')
          return
        }
      }
    } else {
      if (!motherName.trim()) {
        setFormError("Mother's name is required.")
        return
      }
      if (!ppsn.trim()) {
        setFormError('PPS number is required.')
        return
      }
      if (!micsClaimDate.trim()) {
        setFormError('Claim date is required.')
        return
      }
      if (!micsEDD.trim()) {
        setFormError('Expected delivery date is required.')
        return
      }
      if (!micsService.trim()) {
        setFormError('Service provided is required.')
        return
      }
    }
    const sigUrl = getSignatureDataUrl() || (useStoredSignatureView ? storedSignatureDataUrl : null)
    if (!patientSignedOnPaper && !sigUrl) {
      setFormError(useStoredSignatureView ? 'Previously captured signature could not be loaded.' : 'Please provide a signature.')
      return
    }
    const resolvedConsultationId = consultation?.id ?? consultationIdProp
    const resolvedPatientId = patient?.id ?? patientIdProp
    const claimTimeFromConsultation = date.includes('T')
      ? date.split('T')[1]?.slice(0, 5).replace(':', '') || undefined
      : undefined

    const ppsLookupStatusForFormData: StcFormData['ppsLookupStatus'] = claimMode === 'private'
      ? (
          ppsLookupUi.status === 'valid'
            ? 'found'
            : ppsLookupUi.status === 'not_found'
              ? 'not_found'
              : ppsLookupUi.status === 'invalid'
                ? 'invalid'
                : undefined
        )
      : undefined
    const gmsClaimPpsn = String(patient?.ppsNumber || existingRecord?.formData?.ppsn || '').trim().toUpperCase()
    const gmsClaimDateOfBirth = normalizeDateInput(patient?.dateOfBirth || existingRecord?.formData?.dateOfBirth)
    const gmsClaimGender = mapPatientGenderToStcCode(patient?.gender) || normalizeStcGenderCode(existingRecord?.formData?.gender) || undefined
    const formPatientName = formType === 'MICS'
      ? (motherName.trim() || undefined)
      : (patientName.trim() || undefined)
    const formPpsn = formType === 'MICS'
      ? (ppsn.trim().toUpperCase() || undefined)
      : (claimMode === 'private' ? (ppsn.trim() || undefined) : (gmsClaimPpsn || undefined))
    const formData = {
      claimMode,
      gmsNumber: claimMode === 'gms' ? (gmsNumber.trim() || undefined) : undefined,
      ppsn: formPpsn,
      ppsLookupStatus: ppsLookupStatusForFormData,
      dateOfBirth: claimMode === 'private' ? (privateDob || undefined) : (gmsClaimDateOfBirth || undefined),
      gender: claimMode === 'private' ? (privateGender || undefined) : gmsClaimGender,
      stcType,
      claimType,
      location,
      distance,
      visitClass,
      ehicNoPrescriptionRequired,
      ehicPrescriptionSerialNumber: ehicPrescriptionSerialNumber.trim() || undefined,
      ehicCountryCde,
      ehicPatientForeName: ehicPatientForeName.trim() || undefined,
      ehicPatientSurname: ehicPatientSurname.trim() || undefined,
      ehicDob: ehicDob || undefined,
      ehicNumber: ehicNumber.trim() || undefined,
      ehicPersonalIdNumber: ehicPersonalIdNumber.trim() || undefined,
      ehicInstitutionNumber: ehicInstitutionNumber.trim() || undefined,
      ehicPatientExpiryDate: ehicPatientExpiryDate || undefined,
      patientName: formPatientName,
      date,
      claimTime: claimTimeFromConsultation,
      specialService: specialServiceForPayload,
      oohPatientConfirmed,
      oohSignaturePresent,
      patientSignedOnPaper,
      oohGpConfirmed,
      oohCoopRota,
      repName,
      motherName,
      micsClaimDate,
      micsEDD,
      micsService,
      sickCertIcd10PrimaryCode: sickCertIcd10PrimaryCode.trim() || undefined,
      sickCertIcd10Description: sickCertIcd10Description.trim() || undefined,
    }
    const sigUrlForPng = sigUrl || (patientSignedOnPaper ? buildPaperSignatureDataUrl() : null)
    if (saveInFlightRef.current) return
    saveInFlightRef.current = true
    setIsSaving(true)
    try {
      const savedRecord = await stcService.createOrUpdate({
        ...(recordIdProp && { id: recordIdProp }),
        ...(resolvedConsultationId ? { consultationId: resolvedConsultationId } : {}),
        ...(resolvedPatientId ? { patientId: resolvedPatientId } : {}),
        ...(appointmentIdProp && { appointmentId: appointmentIdProp }),
        formType,
        status: embedded ? (existingRecord?.status ?? 'signed') : 'signed',
        signedAt: new Date().toISOString(),
        signatureDataUrl: patientSignedOnPaper ? null : sigUrl,
        formData,
      })
      if (claimMode === 'private' && patient?.id) {
        const patientUpdate: Partial<PatientFormData> = {}
        const normalizedDob = normalizeDateInput(privateDob)
        const mappedPatientGender = mapStcCodeToPatientGender(privateGender)
        if (normalizedDob && normalizedDob !== normalizeDateInput(patient.dateOfBirth)) {
          patientUpdate.dateOfBirth = normalizedDob
        }
        if (mappedPatientGender && mappedPatientGender !== patient.gender) {
          patientUpdate.gender = mappedPatientGender
        }
        if (Object.keys(patientUpdate).length > 0) {
          try {
            await patientService.update(patient.id, patientUpdate)
          } catch (patientUpdateError) {
            console.warn('STC save succeeded but patient demographics update failed', {
              patientId: patient.id,
              claimMode,
              hasDateOfBirth: !!patientUpdate.dateOfBirth,
              hasGender: !!patientUpdate.gender,
              error: patientUpdateError instanceof Error ? patientUpdateError.message : 'Unknown error',
            })
            setSaveWarning('STC record saved, but patient demographics could not be updated.')
          }
        }
      }
      setSaveSuccess(true)
      if (sigUrlForPng) {
        const pngDataUrl = await renderStcFormPng(
          {
            formType,
            ...(formType === 'STC'
              ? {
                  patientName,
                  stcType,
                  claimType,
                  location,
                  distance,
                  date,
                  visitClass,
                  specialService: specialServiceForPayload,
                  representative: repName || '',
                  ehicNoPrescriptionRequired,
                  ehicPrescriptionSerialNumber,
                  ehicCountryCde,
                  ehicPatientForeName,
                  ehicPatientSurname,
                  ehicDob,
                  ehicNumber,
                  ehicPersonalIdNumber,
                  ehicInstitutionNumber,
                  ehicPatientExpiryDate,
                  sickCertIcd10PrimaryCode: sickCertIcd10PrimaryCode.trim() || undefined,
                  sickCertIcd10Description: sickCertIcd10Description.trim() || undefined,
                }
              : { motherName, ppsn, micsClaimDate, micsEDD, micsService }),
          },
          sigUrlForPng
        )
        if (isPptOnly) {
          await stcService.createOrUpdate({
            id: savedRecord.id,
            ...(savedRecord.consultationId ? { consultationId: savedRecord.consultationId } : {}),
            ...(savedRecord.patientId ? { patientId: savedRecord.patientId } : {}),
            formType: savedRecord.formType,
            status: savedRecord.status,
            formData: savedRecord.formData,
            signedAt: savedRecord.signedAt ?? undefined,
            signatureS3Key: savedRecord.signatureS3Key ?? undefined,
            formPngDataUrl: pngDataUrl,
          })
        } else if (patient) {
          const res = await fetch(pngDataUrl)
          const blob = await res.blob()
          const file = new File([blob], `STC-${formType}-${date.slice(0, 10)}.png`, { type: 'image/png' })
          const uploadedFile = await patientFileService.upload(patient.id, {
            file,
            displayName: `STC ${formType} form – ${date.slice(0, 10)}`,
            category: 'form',
          })
          if (uploadedFile.s3Key) {
            await stcService.createOrUpdate({
              id: savedRecord.id,
              ...(savedRecord.consultationId ? { consultationId: savedRecord.consultationId } : {}),
              ...(savedRecord.patientId ? { patientId: savedRecord.patientId } : {}),
              formType: savedRecord.formType,
              status: savedRecord.status,
              formData: savedRecord.formData,
              signedAt: savedRecord.signedAt ?? undefined,
              signatureS3Key: savedRecord.signatureS3Key ?? undefined,
              formPngS3Key: uploadedFile.s3Key,
            })
          }
        }
      }
      if (embedded) {
        onSuccess?.()
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save STC record.')
    } finally {
      saveInFlightRef.current = false
      setIsSaving(false)
    }
  }, [
    formType,
    patientName,
    gmsNumber,
    ppsn,
    privateDob,
    privateGender,
    claimMode,
    ppsLookupUi.status,
    stcType,
    claimType,
    location,
    distance,
    date,
    specialService,
    isEhicResident,
    ehicNoPrescriptionRequired,
    ehicPrescriptionSerialNumber,
    ehicCountryCde,
    ehicPatientForeName,
    ehicPatientSurname,
    ehicDob,
    ehicNumber,
    ehicPersonalIdNumber,
    ehicInstitutionNumber,
    ehicPatientExpiryDate,
    isOutOfHoursVisit,
    oohPatientConfirmed,
    oohSignaturePresent,
    patientSignedOnPaper,
    oohGpConfirmed,
    motherName,
    micsClaimDate,
    micsEDD,
    micsService,
    sickCertIcd10PrimaryCode,
    sickCertIcd10Description,
    repName,
    buildPaperSignatureDataUrl,
    getSignatureDataUrl,
    consultation,
    consultationIdProp,
    patient,
    patientIdProp,
    isPptOnly,
    embedded,
    recordIdProp,
    existingRecord?.status,
    useStoredSignatureView,
    storedSignatureDataUrl,
    onSuccess,
  ])

  const runEligibilityAndLookup = useCallback(async (opts?: {
    gmsValue?: string
    enteredPatientName?: string
    silent?: boolean
    force?: boolean
  }) => {
    if (claimMode !== 'gms') {
      return
    }
    const silent = opts?.silent === true
    const force = opts?.force === true
    const rawGms = String(opts?.gmsValue ?? gmsNumber).trim()
    const enteredPatientName = String(opts?.enteredPatientName ?? patientName).trim()

    if (!rawGms) {
      if (!silent) alert('Please enter a GMS number first.')
      return
    }

    const parsedGms = parseGmsNumberForLookup(rawGms)
    if (!parsedGms) {
      const msg = 'Please enter a valid GMS number (e.g. TEST123A or TEST123).'
      setFormError(msg)
      if (!silent) alert(msg)
      return
    }

    const runKey = `${rawGms.toUpperCase()}|${normalizePersonName(enteredPatientName)}`
    if (!force && runKey === lastEligibilityLookupKeyRef.current) {
      return
    }

    setIsLookupLoading(true)
    setFormError(null)
    setGmsEligibilityUi({ status: 'checking', text: 'Checking...' })
    let terminalUiSet = false
    try {
      const eligibility = await gmsService.checkEligibility(rawGms)
      const eligibilityStatus = String(eligibility.eligibilityStatus || '').toLowerCase()
      if (!eligibility.found) {
        terminalUiSet = true
        setGmsEligibilityUi({ status: 'invalid', text: 'Card not found' })
        throw new Error('Card not found in eligibility checker.')
      }
      if (isEligibilityExpired(eligibility.eligibilityEndDate) || eligibilityStatus.includes('expired')) {
        const expiryDisplay = formatDateToDmy(eligibility.eligibilityEndDate) || eligibility.eligibilityEndDate
        terminalUiSet = true
        setGmsEligibilityUi({
          status: 'expired',
          text: 'Expired',
          expiry: expiryDisplay,
        })
        throw new Error(`Card has expired${expiryDisplay ? ` (${expiryDisplay})` : ''}.`)
      }
      if (eligibilityStatus.includes('not') && eligibilityStatus.includes('eligible')) {
        terminalUiSet = true
        setGmsEligibilityUi({ status: 'invalid', text: 'Not eligible' })
        throw new Error(`Card is not eligible${eligibility.eligibilityStatus ? ` (${eligibility.eligibilityStatus})` : ''}.`)
      }

      const lookupResult = await pcrsService.lookupPatient(parsedGms)
      if (!lookupResult.found) {
        terminalUiSet = true
        setGmsEligibilityUi({ status: 'invalid', text: 'No PCRS match' })
        throw new Error('No patient found in PCRS for this GMS number.')
      }

      const pcrsName = [lookupResult.namFore, lookupResult.namSur].filter(Boolean).join(' ').trim()
      if (enteredPatientName && pcrsName && normalizePersonName(enteredPatientName) !== normalizePersonName(pcrsName)) {
        terminalUiSet = true
        setGmsEligibilityUi({ status: 'invalid', text: 'Name mismatch', pcrsName })
        const pcrsDob = formatDateToDmy(lookupResult.dateOfBirth) || 'Unknown'
        throw new Error(
          `Patient name mismatch. Entered "${enteredPatientName}" but PCRS has "${pcrsName}" (DOB: ${pcrsDob}).`
        )
      }

      lastEligibilityLookupKeyRef.current = runKey
      const expiryDisplay = formatDateToDmy(eligibility.eligibilityEndDate) || eligibility.eligibilityEndDate
      setGmsEligibilityUi({
        status: 'valid',
        text: 'Valid',
        expiry: expiryDisplay,
      })
      if (!silent) {
        const lines = [
          `Eligibility: ${eligibility.eligibilityStatus || 'Valid'}`,
          expiryDisplay ? `Eligibility End: ${expiryDisplay}` : null,
          `Name: ${pcrsName || '—'}`,
          `Card: ${lookupResult.cardNum ?? '—'}`,
          `DOB: ${formatDateToDmy(lookupResult.dateOfBirth) || '—'}`,
          `Gender: ${lookupResult.gender ?? '—'}`,
        ].filter(Boolean)
        alert(lines.join('\n'))
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Eligibility check and patient lookup failed. Please try again.'
      setFormError(msg)
      if (!terminalUiSet) {
        setGmsEligibilityUi({ status: 'error', text: 'Check failed' })
      }
      if (!silent) alert(msg)
    } finally {
      setIsLookupLoading(false)
    }
  }, [claimMode, gmsNumber, patientName])

  const handleGmsLookup = useCallback(async () => {
    await runEligibilityAndLookup({ silent: false, force: true })
  }, [runEligibilityAndLookup])

  const handlePpsLookup = useCallback(async (opts?: { force?: boolean }) => {
    const force = opts?.force === true
    const rawPpsn = String(ppsn || '').trim()
    if (!rawPpsn) {
      setPpsLookupUi({ status: 'invalid', text: 'Enter PPS number first' })
      return
    }
    const lookupKey = rawPpsn.toUpperCase()
    if (!force && lookupKey === lastPpsLookupValueRef.current) {
      return
    }
    setIsLookupLoading(true)
    setFormError(null)
    setPpsLookupUi({ status: 'checking', text: 'Checking...' })
    try {
      const lookupResult = await pcrsService.lookupPatient({ ppsn: rawPpsn })
      if (lookupResult.lookupStatus === 'invalid') {
        setPpsLookupUi({ status: 'invalid', text: 'Invalid' })
        return
      }
      if (!lookupResult.found || lookupResult.lookupStatus === 'not_found') {
        setPpsLookupUi({ status: 'not_found', text: 'Not Found' })
        return
      }
      lastPpsLookupValueRef.current = lookupKey
      const pcrsName = [lookupResult.namFore, lookupResult.namSur].filter(Boolean).join(' ').trim()
      if (pcrsName) setPatientName(pcrsName)
      const dobFromLookup = normalizeDateInput(lookupResult.dateOfBirth)
      if (dobFromLookup) setPrivateDob(dobFromLookup)
      const genderFromLookup = normalizeStcGenderCode(lookupResult.gender)
      if (genderFromLookup) setPrivateGender(genderFromLookup)
      setPpsLookupUi({
        status: 'valid',
        text: 'Matched',
        details: `Name: ${pcrsName || '—'} • DOB: ${formatDateToDmy(lookupResult.dateOfBirth) || '—'} • Gender: ${lookupResult.gender ?? '—'}`,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'PPS lookup failed. Please try again.'
      setFormError(msg)
      setPpsLookupUi({ status: 'error', text: 'Lookup failed' })
    } finally {
      setIsLookupLoading(false)
    }
  }, [ppsn])

  const handlePpsNumberBlur = useCallback(() => {
    if (claimMode !== 'private' || !isStandalonePptMode || !ppsn.trim()) return
    void handlePpsLookup()
  }, [claimMode, handlePpsLookup, isStandalonePptMode, ppsn])

  const handleUsePcrsName = useCallback(() => {
    if (gmsEligibilityUi.status !== 'invalid' || gmsEligibilityUi.text !== 'Name mismatch' || !gmsEligibilityUi.pcrsName) return
    setPatientName(gmsEligibilityUi.pcrsName)
    setGmsEligibilityUi({ status: 'idle', text: '' })
    setFormError(null)
    setShowPatientNameSuggestions(false)
    setAllowNameTypeahead(false)
    window.setTimeout(() => setAllowNameTypeahead(true), 600)
  }, [gmsEligibilityUi])

  const handlePreview = useCallback(() => {
    const msg =
      formType === 'STC'
        ? `STC\nPatient: ${patientName}\nDate: ${date}\nVisit Class: ${visitClass}\nDistance: ${distance}\nSpecial Service: ${specialServiceForPayload}\nRepresentative: ${repName || '—'}${
          isEhicResident ? `\nEHIC State: ${ehicCountryCde}\nEHIC Number: ${ehicNumber || '—'}` : ''
        }`
        : `MICS\nMother: ${motherName}\nPPS: ${ppsn || '—'}\nClaim date: ${micsClaimDate}\nEDD: ${micsEDD}\nService: ${micsService}`
    alert(msg)
  }, [formType, patientName, date, visitClass, distance, specialServiceForPayload, repName, isEhicResident, ehicCountryCde, ehicNumber, motherName, ppsn, micsClaimDate, micsEDD, micsService])

  const applyPrefillFromSuggestion = useCallback(async (suggestion: StcTypeaheadSuggestion | null) => {
    if (!suggestion?.id || suggestion.id === activeSuggestionId) return
    setFormError(null)
    setActiveSuggestionId(suggestion.id)
    const applyData = (fd: Record<string, any>, record?: Partial<StcRecord>) => {
      const resolvedPatientName = fd.patientName ?? record?.patientName ?? suggestion.patientName
      const resolvedGmsNumber = fd.gmsNumber ?? record?.gmsNumber ?? suggestion.gmsNumber ?? ''
      const resolvedPpsn = fd.ppsn ?? suggestion.ppsn ?? suggestion.formData?.ppsn ?? ''
      const resolvedDateOfBirth = fd.dateOfBirth ?? suggestion.dateOfBirth ?? suggestion.formData?.dateOfBirth
      const resolvedGender = fd.gender ?? suggestion.formData?.gender
      if (resolvedPatientName !== undefined) setPatientName(resolvedPatientName)
      if (resolvedGmsNumber !== undefined) setGmsNumber(resolvedGmsNumber)
      if (resolvedPpsn !== undefined) setPpsn(String(resolvedPpsn))
      if (resolvedDateOfBirth !== undefined) setPrivateDob(normalizeDateInput(String(resolvedDateOfBirth)))
      if (resolvedGender !== undefined) {
        const normalizedGender = normalizeStcGenderCode(String(resolvedGender))
        if (normalizedGender) setPrivateGender(normalizedGender)
      }
      if (fd.claimMode === 'gms' || fd.claimMode === 'private') {
        setClaimMode(fd.claimMode)
      } else if (resolvedPpsn && !resolvedGmsNumber) {
        setClaimMode('private')
      } else if (resolvedGmsNumber) {
        setClaimMode('gms')
      }

      if (fd.stcType !== undefined) setStcType(fd.stcType)
      if (fd.claimType !== undefined) setClaimType(fd.claimType)
      if (fd.location !== undefined) setLocation(fd.location)
      if (fd.distance !== undefined) setDistance(fd.distance)
      if (fd.visitClass !== undefined) setVisitClass(fd.visitClass)
      if (fd.ehicNoPrescriptionRequired !== undefined) setEhicNoPrescriptionRequired(fd.ehicNoPrescriptionRequired)
      if (fd.ehicPrescriptionSerialNumber !== undefined) setEhicPrescriptionSerialNumber(fd.ehicPrescriptionSerialNumber)
      if (fd.ehicCountryCde !== undefined) setEhicCountryCde(fd.ehicCountryCde)
      if (fd.ehicPatientForeName !== undefined) setEhicPatientForeName(fd.ehicPatientForeName)
      if (fd.ehicPatientSurname !== undefined) setEhicPatientSurname(fd.ehicPatientSurname)
      if (fd.ehicDob !== undefined) setEhicDob(fd.ehicDob)
      if (fd.ehicNumber !== undefined) setEhicNumber(fd.ehicNumber)
      if (fd.ehicPersonalIdNumber !== undefined) setEhicPersonalIdNumber(fd.ehicPersonalIdNumber)
      if (fd.ehicInstitutionNumber !== undefined) setEhicInstitutionNumber(fd.ehicInstitutionNumber)
      if (fd.ehicPatientExpiryDate !== undefined) setEhicPatientExpiryDate(fd.ehicPatientExpiryDate)
      if (fd.date !== undefined) setDate(fd.date)
      if (fd.specialService !== undefined) setSpecialService(fd.specialService)
      if (fd.oohPatientConfirmed !== undefined) setOohPatientConfirmed(fd.oohPatientConfirmed)
      if (fd.oohSignaturePresent !== undefined) setOohSignaturePresent(fd.oohSignaturePresent)
      if (fd.patientSignedOnPaper !== undefined) setPatientSignedOnPaper(Boolean(fd.patientSignedOnPaper))
      if (fd.oohGpConfirmed !== undefined) setOohGpConfirmed(fd.oohGpConfirmed)
      if (fd.oohCoopRota !== undefined) setOohCoopRota(fd.oohCoopRota)
      if (fd.repName !== undefined) setRepName(fd.repName)
      if (fd.motherName !== undefined) setMotherName(fd.motherName)
      if (fd.micsClaimDate !== undefined) setMicsClaimDate(fd.micsClaimDate)
      if (fd.micsEDD !== undefined) setMicsEDD(fd.micsEDD)
      if (fd.micsService !== undefined) setMicsService(fd.micsService)
      if (fd.sickCertIcd10PrimaryCode !== undefined) setSickCertIcd10PrimaryCode(fd.sickCertIcd10PrimaryCode)
      if (fd.sickCertIcd10Description !== undefined) setSickCertIcd10Description(fd.sickCertIcd10Description)
    }

    // Apply any inline suggestion data immediately (patient seeded rows may not resolve via STC getById).
    applyData((suggestion.formData || {}) as Record<string, any>)

    try {
      const record = await stcService.getById(suggestion.id)
      const fd = record.formData || {}
      applyData(fd as Record<string, any>, record)
    } catch {
      // Selection may point to PPT patient seed data rather than an STC record id.
    }
  }, [activeSuggestionId])

  const handlePatientNameBlur = useCallback(() => {
    setTimeout(() => setShowPatientNameSuggestions(false), 120)
    if (usePcrsNameButtonPressedRef.current) {
      usePcrsNameButtonPressedRef.current = false
      return
    }
    if (!isStandalonePptMode || !patientName.trim()) return
    const normalizedPatientName = patientName.trim().toLowerCase()
    const matchesByName = patientNameSuggestions.filter(
      (item) => (item.patientName || '').trim().toLowerCase() === normalizedPatientName
    )
    let match: StcTypeaheadSuggestion | null = null
    if (matchesByName.length === 1) {
      match = matchesByName[0]
    } else if (matchesByName.length > 1 && activeSuggestionId) {
      match = matchesByName.find((item) => item.id === activeSuggestionId) || null
    }
    void applyPrefillFromSuggestion(match)
  }, [activeSuggestionId, applyPrefillFromSuggestion, isStandalonePptMode, patientName, patientNameSuggestions])

  const handleSelectTypeaheadSuggestion = useCallback((suggestion: StcTypeaheadSuggestion) => {
    if (formType === 'MICS') {
      const resolvedName = String(
        suggestion.patientName || (suggestion.formData?.patientName as string) || ''
      ).trim()
      const resolvedPpsn = String(
        suggestion.ppsn || (suggestion.formData?.ppsn as string) || ''
      ).trim()
      if (resolvedName) setMotherName(resolvedName)
      if (resolvedPpsn) setPpsn(resolvedPpsn)
      setShowMicsNameSuggestions(false)
      setShowPatientNameSuggestions(false)
      setShowGmsSuggestions(false)
      setActiveMicsNameSuggestionIndex(-1)
      setActivePatientSuggestionIndex(-1)
      setActiveGmsSuggestionIndex(-1)
      return
    }
    if (suggestion.patientName) setPatientName(suggestion.patientName)
    if (suggestion.gmsNumber) setGmsNumber(suggestion.gmsNumber)
    setShowPatientNameSuggestions(false)
    setShowGmsSuggestions(false)
    setActivePatientSuggestionIndex(-1)
    setActiveGmsSuggestionIndex(-1)
    void applyPrefillFromSuggestion(suggestion).then(() => {
      if (claimMode !== 'gms' || !suggestion.gmsNumber) return
      void runEligibilityAndLookup({
        gmsValue: suggestion.gmsNumber,
        enteredPatientName: suggestion.patientName || patientName,
        silent: true,
        force: true,
      })
    })
  }, [applyPrefillFromSuggestion, claimMode, formType, patientName, runEligibilityAndLookup])

  const handlePatientNameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isStandalonePptMode || patientNameSuggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setShowPatientNameSuggestions(true)
      setActivePatientSuggestionIndex((prev) => (prev + 1) % patientNameSuggestions.length)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setShowPatientNameSuggestions(true)
      setActivePatientSuggestionIndex((prev) => (prev <= 0 ? patientNameSuggestions.length - 1 : prev - 1))
      return
    }
    if (e.key === 'Enter') {
      if (!showPatientNameSuggestions || activePatientSuggestionIndex < 0) return
      e.preventDefault()
      const selected = patientNameSuggestions[activePatientSuggestionIndex]
      if (selected) handleSelectTypeaheadSuggestion(selected)
      return
    }
    if (e.key === 'Escape') {
      setShowPatientNameSuggestions(false)
    }
  }, [activePatientSuggestionIndex, handleSelectTypeaheadSuggestion, isStandalonePptMode, patientNameSuggestions, showPatientNameSuggestions])

  const handleGmsNumberBlur = useCallback(() => {
    setTimeout(() => setShowGmsSuggestions(false), 120)
    if (claimMode !== 'gms' || !isStandalonePptMode || !gmsNumber.trim()) return
    const match = gmsSuggestions.find(
      (item) => (item.gmsNumber || '').trim().toUpperCase() === gmsNumber.trim().toUpperCase()
    ) || null
    void applyPrefillFromSuggestion(match)
    void runEligibilityAndLookup({ silent: true })
  }, [applyPrefillFromSuggestion, claimMode, gmsNumber, gmsSuggestions, isStandalonePptMode, runEligibilityAndLookup])

  const handleGmsNumberKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (claimMode !== 'gms' || !isStandalonePptMode || gmsSuggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setShowGmsSuggestions(true)
      setActiveGmsSuggestionIndex((prev) => (prev + 1) % gmsSuggestions.length)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setShowGmsSuggestions(true)
      setActiveGmsSuggestionIndex((prev) => (prev <= 0 ? gmsSuggestions.length - 1 : prev - 1))
      return
    }
    if (e.key === 'Enter') {
      if (!showGmsSuggestions || activeGmsSuggestionIndex < 0) return
      e.preventDefault()
      const selected = gmsSuggestions[activeGmsSuggestionIndex]
      if (selected) handleSelectTypeaheadSuggestion(selected)
      return
    }
    if (e.key === 'Escape') {
      setShowGmsSuggestions(false)
    }
  }, [activeGmsSuggestionIndex, claimMode, gmsSuggestions, handleSelectTypeaheadSuggestion, isStandalonePptMode, showGmsSuggestions])

  const handleMicsNameBlur = useCallback(() => {
    setTimeout(() => setShowMicsNameSuggestions(false), 120)
    if (!isStandalonePptMode || !motherName.trim()) return
    const normalizedMotherName = motherName.trim().toLowerCase()
    const matchesByName = micsNameSuggestions.filter(
      (item) => (item.patientName || '').trim().toLowerCase() === normalizedMotherName
    )
    let match: StcTypeaheadSuggestion | null = null
    if (matchesByName.length === 1) {
      match = matchesByName[0]
    } else if (matchesByName.length > 1 && activeSuggestionId) {
      match = matchesByName.find((item) => item.id === activeSuggestionId) || null
    }
    if (!match) return
    const resolvedName = String(match.patientName || (match.formData?.patientName as string) || '').trim()
    const resolvedPpsn = String(match.ppsn || (match.formData?.ppsn as string) || '').trim()
    if (resolvedName) setMotherName(resolvedName)
    if (resolvedPpsn) setPpsn(resolvedPpsn)
  }, [activeSuggestionId, isStandalonePptMode, micsNameSuggestions, motherName])

  const handleMicsNameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isStandalonePptMode || micsNameSuggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setShowMicsNameSuggestions(true)
      setActiveMicsNameSuggestionIndex((prev) => (prev + 1) % micsNameSuggestions.length)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setShowMicsNameSuggestions(true)
      setActiveMicsNameSuggestionIndex((prev) => (prev <= 0 ? micsNameSuggestions.length - 1 : prev - 1))
      return
    }
    if (e.key === 'Enter') {
      if (!showMicsNameSuggestions || activeMicsNameSuggestionIndex < 0) return
      e.preventDefault()
      const selected = micsNameSuggestions[activeMicsNameSuggestionIndex]
      if (selected) handleSelectTypeaheadSuggestion(selected)
      return
    }
    if (e.key === 'Escape') {
      setShowMicsNameSuggestions(false)
    }
  }, [activeMicsNameSuggestionIndex, handleSelectTypeaheadSuggestion, isStandalonePptMode, micsNameSuggestions, showMicsNameSuggestions])

  const handleDownload = useCallback(async () => {
    const sigUrl = getSignatureDataUrl() || (useStoredSignatureView ? storedSignatureDataUrl : null)
    if (!patientSignedOnPaper && !sigUrl) {
      setFormError(useStoredSignatureView ? 'Previously captured signature could not be loaded.' : 'Please provide a signature before downloading.')
      return
    }
    setFormError(null)
    const sigUrlForPng = sigUrl || (patientSignedOnPaper ? buildPaperSignatureDataUrl() : null)
    if (!sigUrlForPng) {
      setFormError('Failed to prepare signature image.')
      return
    }
    const data: StcFormDataForPng = {
      formType,
      ...(formType === 'STC'
        ? {
            patientName,
            stcType,
            claimType,
            location,
            distance,
            date,
            visitClass,
            specialService: specialServiceForPayload,
            representative: repName || '',
            ehicNoPrescriptionRequired,
            ehicPrescriptionSerialNumber,
            ehicCountryCde,
            ehicPatientForeName,
            ehicPatientSurname,
            ehicDob,
            ehicNumber,
            ehicPersonalIdNumber,
            ehicPatientExpiryDate,
            sickCertIcd10PrimaryCode: sickCertIcd10PrimaryCode.trim() || undefined,
            sickCertIcd10Description: sickCertIcd10Description.trim() || undefined,
          }
        : {
            motherName,
            micsClaimDate,
            micsEDD,
            micsService,
          }),
    }
    try {
      const pngDataUrl = await renderStcFormPng(data, sigUrlForPng)
      const link = document.createElement('a')
      link.href = pngDataUrl
      link.download = `stc-${formType}-${nowLocalDate()}.png`
      link.click()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to generate form image.')
    }
  }, [
    formType,
    patientName,
    stcType,
    claimType,
    location,
    distance,
    date,
    visitClass,
    specialServiceForPayload,
    repName,
    ehicNoPrescriptionRequired,
    ehicPrescriptionSerialNumber,
    ehicCountryCde,
    ehicPatientForeName,
    ehicPatientSurname,
    ehicDob,
    ehicNumber,
    ehicPersonalIdNumber,
    ehicInstitutionNumber,
    ehicPatientExpiryDate,
    sickCertIcd10PrimaryCode,
    sickCertIcd10Description,
    motherName,
    micsClaimDate,
    micsEDD,
    micsService,
    patientSignedOnPaper,
    buildPaperSignatureDataUrl,
    useStoredSignatureView,
    storedSignatureDataUrl,
    getSignatureDataUrl,
  ])

  const isLoading = !isStandalonePptMode && (loadingConsultation || loadingPatient)
  const error = consultationError || patientError

  if (!consultationId && !embedded && !isStandalonePptMode) {
    return (
      <div className="stc-signing-page">
        <p>Invalid consultation.</p>
      </div>
    )
  }

  if (!embedded && !isStandalonePptMode && (isLoading || error || !consultation || !patient)) {
    if (isLoading) {
      return (
        <div className="stc-signing-page">
          <p>Loading…</p>
        </div>
      )
    }
    if (error || !consultation || !patient) {
      return (
        <div className="stc-signing-page">
          <p>Could not load consultation or patient.</p>
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      )
    }
  }

  if (embedded && !isStandalonePptMode && (isLoading || !consultation || !patient)) {
    return (
      <div className="stc-signing-page">
        <p>Loading…</p>
      </div>
    )
  }

  if (embedded && !isStandalonePptMode && (error || !consultation || !patient)) {
    return (
      <div className="stc-signing-page">
        <p>Could not load consultation or patient.</p>
        {onClose && (
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
        )}
      </div>
    )
  }

  const formTitle = formType === 'MICS' ? 'Mother & Infant Care Scheme (PCRS)' : 'STC Patient Declaration (PCRS)'
  const formSubtitle =
    formType === 'MICS'
      ? 'Evidence of attendance for MICS consultation'
      : 'Evidence of attendance for Special Type Consultation claim'

  return (
    <div className="stc-signing-page" role="main">
      <header className="stc-signing-header">
        <div className="stc-signing-header-left">
          {embedded && onClose ? (
            <button type="button" className="btn-back" onClick={onClose} aria-label="Close">
              Close
            </button>
          ) : (
            <button
              type="button"
              className="btn-back"
              onClick={() => {
                if (patient?.id) {
                  navigate(`/patients/${patient.id}`)
                } else {
                  navigate('/reports/stc-status')
                }
              }}
              aria-label="Back"
            >
              ← Back
            </button>
          )}
          <div>
            <h1>{formTitle}</h1>
            <p className="stc-signing-subtitle">{formSubtitle}</p>
          </div>
        </div>
      </header>

      <div className="stc-signing-card">
        <form className="stc-form" onSubmit={(e) => e.preventDefault()} autoComplete="off">
          {isReadOnlyMode && (
            <>
              <p className="stc-muted" role="status">
                This record is read-only due to its current claim status.
              </p>
              {existingRecord?.status === 'rejected' && existingRecord?.rejectionReason && (
                <div className="stc-rejection-reason" role="status">
                  <strong>Rejection reason:</strong>{' '}
                  <span>{existingRecord.rejectionReason}</span>
                </div>
              )}
            </>
          )}
          <fieldset disabled={isReadOnlyMode}>
          {!embedded && (
            <div className="stc-form-section">
              <label htmlFor="formType">Form Type</label>
              <select
                id="formType"
                value={formType}
                onChange={(e) => setFormType(e.target.value as StcFormType)}
                aria-label="Form type"
              >
                <option value="STC">Special Type Consultation (STC)</option>
                <option value="MICS">Mother &amp; Infant Care Scheme (MICS)</option>
              </select>
            </div>
          )}

          {formType === 'STC' && (
            <>
              <div className="stc-form-section">
                <label>Type of Claim</label>
                <div className="stc-claim-mode-toggle" role="radiogroup" aria-label="Type of Claim">
                  <label className="stc-claim-mode-option">
                    <input
                      type="radio"
                      name="claimMode"
                      value="gms"
                      checked={claimMode === 'gms'}
                      onChange={() => setClaimMode('gms')}
                    />
                    <span>Standard GMS Claim</span>
                  </label>
                  <label className="stc-claim-mode-option">
                    <input
                      type="radio"
                      name="claimMode"
                      value="private"
                      checked={claimMode === 'private'}
                      onChange={() => setClaimMode('private')}
                    />
                    <span>Private Patient</span>
                  </label>
                </div>
                <p className="stc-muted">Hotkeys: Ctrl+G (GMS), Ctrl+P (Private)</p>
              </div>

              <div className="stc-form-section">
                <div className="stc-form-grid two">
                  <div>
                    <label htmlFor="stcType">STC Type</label>
                    <select
                      id="stcType"
                      name="stcType"
                      className="stc-type"
                      value={stcType}
                      onChange={(e) => {
                        const v = e.target.value
                        setStcType(v)
                        if (v === 'E') setSpecialService('0')
                      }}
                      aria-label="STC type"
                    >
                      {STC_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="specialService">
                      Special Service{' '}
                      <span className="stc-muted">{isEmergencyStcType ? '(optional)' : '(required)'}</span>
                    </label>
                    <select
                      ref={specialServiceSelectRef}
                      id="specialService"
                      name="specialService.condition"
                      value={specialService}
                      onChange={(e) => setSpecialService(e.target.value)}
                      aria-required={!isEmergencyStcType}
                      aria-label="Special service"
                    >
                      {availableSpecialServiceOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="stc-form-grid stc-form-grid-stc-main">
                <div>
                  <label htmlFor="patientName">
                    Patient Name <span className="stc-muted">(required)</span>
                  </label>
                  <div className={`stc-typeahead-wrap${isStandalonePptMode ? ' stc-typeahead-wrap-with-spinner' : ''}`}>
                    <input
                      ref={patientNameInputRef}
                      id="patientName"
                      type="text"
                      value={patientName}
                      onChange={(e) => {
                        setPatientName(e.target.value)
                        setShowPatientNameSuggestions(true)
                        setActiveSuggestionId(null)
                        setGmsEligibilityUi({ status: 'idle', text: '' })
                        setFormError(null)
                      }}
                      onFocus={() => setShowPatientNameSuggestions(true)}
                      onBlur={handlePatientNameBlur}
                      onKeyDown={handlePatientNameKeyDown}
                      aria-required="true"
                      autoComplete="off"
                      aria-busy={isStandalonePptMode && patientNameQuery.length >= TYPEAHEAD_MIN_CHARS && isPatientNameSearching}
                    />
                    {isStandalonePptMode &&
                      patientNameQuery.length >= TYPEAHEAD_MIN_CHARS &&
                      isPatientNameSearching && (
                        <span className="stc-name-search-spinner-wrap" aria-live="polite" aria-label="Searching">
                          <svg className="gms-lookup-spinner stc-name-search-spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                            <circle cx="9" cy="9" r="7" strokeDasharray="44" strokeDashoffset="11" />
                          </svg>
                        </span>
                      )}
                    {isStandalonePptMode && showPatientNameSuggestions && patientNameSuggestions.length > 0 && (
                      <div className="stc-typeahead-list" role="listbox" aria-label="Patient name suggestions">
                        {patientNameSuggestions.map((s, idx) => (
                          <button
                            key={s.id}
                            type="button"
                            className={`stc-typeahead-item ${idx === activePatientSuggestionIndex ? 'active' : ''}`}
                            onMouseDown={(e) => {
                              e.preventDefault()
                              handleSelectTypeaheadSuggestion(s)
                            }}
                          >
                            <span className="stc-typeahead-primary">{s.patientName || 'Unknown patient'}</span>
                            <span className="stc-typeahead-meta">
                              {s.gmsNumber ? `GMS ${s.gmsNumber}` : 'No GMS'}
                              {s.dateOfBirth || (s.formData?.dateOfBirth as string)
                                ? ` • DOB ${formatTypeaheadDob(s.dateOfBirth || (s.formData?.dateOfBirth as string))}`
                                : ''}
                              {s.ppsn || (s.formData?.ppsn as string)
                                ? ` • PPS ${(s.ppsn || (s.formData?.ppsn as string))}`
                                : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {claimMode === 'gms' ? (
                  <div>
                    <label htmlFor="gmsNumber">
                      <span>GMS Number</span>
                      {gmsEligibilityUi.status !== 'idle' && (
                        <span className={`gms-eligibility-inline gms-eligibility-${gmsEligibilityUi.status}`} aria-live="polite">
                          {(gmsEligibilityUi.status === 'valid' || gmsEligibilityUi.status === 'expired')
                            ? ` (${gmsEligibilityUi.text}${gmsEligibilityUi.expiry ? `, expires ${gmsEligibilityUi.expiry}` : ''})`
                            : ` (${gmsEligibilityUi.text})`
                        }
                      </span>
                      )}
                      {isStandalonePptMode &&
                        gmsEligibilityUi.status === 'invalid' &&
                        gmsEligibilityUi.text === 'Name mismatch' &&
                        gmsEligibilityUi.pcrsName && (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="stc-btn-link stc-label-inline"
                              onMouseDown={() => {
                                usePcrsNameButtonPressedRef.current = true
                              }}
                              onClick={handleUsePcrsName}
                              aria-label="Use PCRS name for form"
                            >
                              Use PCRS name
                            </button>
                          </>
                        )}
                    </label>
                    <div className="gms-lookup-row">
                      <div className="stc-typeahead-wrap">
                        <input
                          id="gmsNumber"
                          type="text"
                          value={gmsNumber}
                          onChange={(e) => {
                            setGmsNumber(e.target.value)
                            setGmsEligibilityUi({ status: 'idle', text: '' })
                            setShowGmsSuggestions(true)
                            setActiveSuggestionId(null)
                          }}
                          onFocus={() => setShowGmsSuggestions(true)}
                          onBlur={handleGmsNumberBlur}
                          onKeyDown={handleGmsNumberKeyDown}
                          placeholder="e.g. TEST123A"
                          aria-label="GMS number"
                          disabled={isLookupLoading}
                          autoComplete="off"
                        />
                        {isStandalonePptMode && showGmsSuggestions && gmsSuggestions.length > 0 && (
                          <div className="stc-typeahead-list" role="listbox" aria-label="GMS suggestions">
                            {gmsSuggestions.map((s, idx) => (
                              <button
                                key={s.id}
                                type="button"
                                className={`stc-typeahead-item ${idx === activeGmsSuggestionIndex ? 'active' : ''}`}
                                onMouseDown={(e) => {
                                  e.preventDefault()
                                  handleSelectTypeaheadSuggestion(s)
                                }}
                              >
                                <span className="stc-typeahead-primary">{s.gmsNumber || 'No GMS'}</span>
                                <span className="stc-typeahead-meta">
                                  {s.patientName || 'Unknown patient'}
                                  {s.dateOfBirth || (s.formData?.dateOfBirth as string)
                                    ? ` • DOB ${formatTypeaheadDob(s.dateOfBirth || (s.formData?.dateOfBirth as string))}`
                                    : ''}
                                  {s.ppsn || (s.formData?.ppsn as string)
                                    ? ` • PPS ${(s.ppsn || (s.formData?.ppsn as string))}`
                                    : ''}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="gms-lookup-btn"
                        onClick={handleGmsLookup}
                        disabled={isLookupLoading || !gmsNumber.trim()}
                        title={isLookupLoading ? 'Checking eligibility and patient lookup…' : 'Check eligibility and look up patient'}
                        aria-label={isLookupLoading ? 'Checking eligibility and patient lookup' : 'Check eligibility and look up patient'}
                        aria-busy={isLookupLoading}
                        style={{
                          padding: '0.375rem 0.5rem',
                          border: '1px solid var(--border, #e5e7eb)',
                          borderRadius: '4px',
                          background: 'var(--bg-secondary, #f9fafb)',
                          cursor: isLookupLoading || !gmsNumber.trim() ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isLookupLoading ? (
                          <svg className="gms-lookup-spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                            <circle cx="9" cy="9" r="7" strokeDasharray="44" strokeDashoffset="11" />
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="ppsn">
                      PPS Number <span className="stc-muted">(required)</span>
                      {ppsLookupUi.status !== 'idle' && (
                        <span className={`gms-eligibility-inline gms-eligibility-${ppsLookupUi.status}`} aria-live="polite">
                          {` (${ppsLookupUi.text})`}
                        </span>
                      )}
                    </label>
                    <div className="gms-lookup-row">
                      <input
                        ref={ppsInputRef}
                        id="ppsn"
                        type="text"
                        value={ppsn}
                        onChange={(e) => {
                          setPpsn(e.target.value)
                          setPpsLookupUi({ status: 'idle', text: '' })
                          lastPpsLookupValueRef.current = ''
                        }}
                        onBlur={handlePpsNumberBlur}
                        placeholder="e.g. MOCKPPS1"
                        aria-label="PPS number"
                        disabled={isLookupLoading}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="gms-lookup-btn"
                        onClick={() => void handlePpsLookup({ force: true })}
                        disabled={isLookupLoading || !ppsn.trim()}
                        title={isLookupLoading ? 'Looking up patient by PPS…' : 'Look up patient by PPS'}
                        aria-label={isLookupLoading ? 'Looking up patient by PPS' : 'Look up patient by PPS'}
                        aria-busy={isLookupLoading}
                        style={{
                          padding: '0.375rem 0.5rem',
                          border: '1px solid var(--border, #e5e7eb)',
                          borderRadius: '4px',
                          background: 'var(--bg-secondary, #f9fafb)',
                          cursor: isLookupLoading || !ppsn.trim() ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isLookupLoading ? (
                          <svg className="gms-lookup-spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                            <circle cx="9" cy="9" r="7" strokeDasharray="44" strokeDashoffset="11" />
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {ppsLookupUi.status === 'valid' && ppsLookupUi.details && (
                      <p className="stc-muted" role="status" aria-live="polite">{ppsLookupUi.details}</p>
                    )}
                    <div className="stc-form-grid two stc-private-demographics">
                      <div>
                        <label htmlFor="privateDob">
                          Date of Birth
                          {ppsLookupUi.status === 'not_found' && <span className="stc-muted"> (required)</span>}
                        </label>
                        <input
                          id="privateDob"
                          type="date"
                          value={privateDob}
                          onChange={(e) => setPrivateDob(e.target.value)}
                        />
                      </div>
                      <div>
                        <label htmlFor="privateGender">Gender</label>
                        <select
                          id="privateGender"
                          value={privateGender}
                          onChange={(e) => setPrivateGender((normalizeStcGenderCode(e.target.value) || 'F') as 'F' | 'M' | 'O' | 'U')}
                        >
                          <option value="F">F - Female</option>
                          <option value="M">M - Male</option>
                          <option value="O">O - Other</option>
                          <option value="U">U - Unknown</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label id="dateLabel" htmlFor="date">
                    {isOutOfHoursVisit ? 'Date of Consultation' : 'Date & Time of Consultation'}{' '}
                    <span className="stc-muted">(required)</span>
                  </label>
                  <input
                    id="date"
                    type={isOutOfHoursVisit ? 'date' : 'datetime-local'}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    aria-required="true"
                    aria-labelledby="dateLabel"
                  />
                </div>
                <div>
                  <label htmlFor="visit">Visit Class</label>
                  <select
                    id="visit"
                    name="visitClass"
                    className="visit-class"
                    value={visitClass}
                    onChange={(e) => setVisitClass(e.target.value)}
                    aria-label="Visit class"
                  >
                    {VISIT_CLASS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.value === 'N'
                          ? stcType === 'H'
                            ? 'N - 5 p.m. to 9 a.m.'
                            : 'N - Midnight to 8 a.m.'
                          : opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="claimTypeId">Type of Claim</label>
                  <select
                    id="claimTypeId"
                    name="claimTypeEnum"
                    className="match-key claim-type"
                    value={claimType}
                    onChange={(e) => setClaimType(e.target.value)}
                    aria-label="Type of claim"
                  >
                    {CLAIM_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="location">Location</label>
                  <select
                    id="location"
                    name="location"
                    className="match-key location"
                    tabIndex={4}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    aria-label="Location"
                  >
                    {LOCATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="stc-form-section">
                <div className="stc-form-grid two">
                  <div>
                    <label htmlFor="distance">Distance</label>
                    <select
                      id="distance"
                      name="distance"
                      className="match-key distance"
                      tabIndex={9}
                      value={distance}
                      onChange={(e) => setDistance(e.target.value)}
                      aria-label="Distance"
                    >
                      {DISTANCE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="repName" className="stc-muted">
                      If signing on behalf of patient, indicate relationship (optional)
                    </label>
                    <select
                      id="repName"
                      value={repName}
                      onChange={(e) => setRepName(e.target.value)}
                      aria-label="Relationship of signatory"
                    >
                      <option value="">Select relationship</option>
                      {RELATIONSHIP_OPTIONS.filter(Boolean).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="stc-form-section">
                <h3 className="stc-ooh-options-title">Sick certificate documentation (optional)</h3>
                <p className="stc-muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                  Primary ICD-10 may pre-fill from the linked consultation when an ICD-10 code is recorded.
                </p>
                <div className="stc-form-grid two">
                  <div>
                    <label htmlFor="sickCertIcd10PrimaryCode">Primary ICD-10 code</label>
                    <input
                      id="sickCertIcd10PrimaryCode"
                      type="text"
                      name="sickCertIcd10PrimaryCode"
                      value={sickCertIcd10PrimaryCode}
                      onChange={(e) => setSickCertIcd10PrimaryCode(e.target.value)}
                      placeholder="e.g. J06.9"
                      aria-label="Primary ICD-10 code for sick certificate"
                    />
                  </div>
                  <div>
                    <label htmlFor="sickCertIcd10Description">Description</label>
                    <input
                      id="sickCertIcd10Description"
                      type="text"
                      name="sickCertIcd10Description"
                      value={sickCertIcd10Description}
                      onChange={(e) => setSickCertIcd10Description(e.target.value)}
                      placeholder="Clinical description"
                      aria-label="ICD-10 description for sick certificate"
                    />
                  </div>
                </div>
              </div>

              {isEhicResident && (
                <div className="stc-form-section">
                  <h3 className="stc-ooh-options-title">European Health Insurance Card Details</h3>
                  <div className="stc-form-grid stc-form-grid-ehic">
                    <div>
                      <label htmlFor="ehicPrescriptionSerialNumber">
                        Prescription Serial Number <span className="stc-muted">(required unless no prescription)</span>
                      </label>
                      <input
                        id="ehicPrescriptionSerialNumber"
                        type="text"
                        value={ehicPrescriptionSerialNumber}
                        onChange={(e) => setEhicPrescriptionSerialNumber(e.target.value)}
                        disabled={ehicNoPrescriptionRequired}
                      />
                    </div>
                    <div className="stc-checkbox-wrap stc-ehic-checkbox-wrap">
                      <input
                        id="ehicNoPrescriptionRequired"
                        type="checkbox"
                        checked={ehicNoPrescriptionRequired}
                        onChange={(e) => setEhicNoPrescriptionRequired(e.target.checked)}
                      />
                      <label htmlFor="ehicNoPrescriptionRequired" className="stc-checkbox-label">
                        No Prescription Required for this GP Visit
                      </label>
                    </div>
                    <div>
                      <label htmlFor="ehicCountryCde">
                        State Identifier <span className="stc-muted">(required)</span>
                      </label>
                      <select
                        id="ehicCountryCde"
                        value={ehicCountryCde}
                        onChange={(e) => setEhicCountryCde(e.target.value)}
                      >
                        {EHIC_COUNTRY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="ehicPatientForeName">
                        Given Name <span className="stc-muted">(required)</span>
                      </label>
                      <input
                        id="ehicPatientForeName"
                        type="text"
                        value={ehicPatientForeName}
                        onChange={(e) => setEhicPatientForeName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="ehicPatientSurname">
                        Name <span className="stc-muted">(required)</span>
                      </label>
                      <input
                        id="ehicPatientSurname"
                        type="text"
                        value={ehicPatientSurname}
                        onChange={(e) => setEhicPatientSurname(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="ehicDob">
                        Date of Birth <span className="stc-muted">(required)</span>
                      </label>
                      <input
                        id="ehicDob"
                        type="date"
                        value={ehicDob}
                        onChange={(e) => setEhicDob(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="ehicNumber">
                        Identification Number <span className="stc-muted">(required)</span>
                      </label>
                      <input
                        id="ehicNumber"
                        type="text"
                        value={ehicNumber}
                        onChange={(e) => setEhicNumber(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="ehicPersonalIdNumber">
                        Personal ID Number <span className="stc-muted">(required)</span>
                      </label>
                      <input
                        id="ehicPersonalIdNumber"
                        type="text"
                        value={ehicPersonalIdNumber}
                        onChange={(e) => setEhicPersonalIdNumber(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="ehicInstitutionNumber">
                        Institution Number <span className="stc-muted">(required)</span>
                      </label>
                      <input
                        id="ehicInstitutionNumber"
                        type="text"
                        value={ehicInstitutionNumber}
                        onChange={(e) => setEhicInstitutionNumber(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="ehicPatientExpiryDate">
                        Card Expiry Date <span className="stc-muted">(required)</span>
                      </label>
                      <input
                        id="ehicPatientExpiryDate"
                        type="date"
                        value={ehicPatientExpiryDate}
                        onChange={(e) => setEhicPatientExpiryDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {isOutOfHoursVisit && (
                <div className="stc-form-section stc-ooh-options">
                  <h3 className="stc-ooh-options-title">Out of Hours Options</h3>
                  <div className="stc-ooh-checkboxes">
                    <label className="stc-ooh-checkbox-label">
                      <input
                        type="checkbox"
                        checked={oohPatientConfirmed}
                        onChange={(e) => setOohPatientConfirmed(e.target.checked)}
                        aria-required={isOutOfHoursVisit}
                        aria-describedby="ooh-patient-desc"
                      />
                      <span id="ooh-patient-desc">
                        Has the patient confirmed Non-Routine Out-of-Hours consultation? (Y/N) <span className="stc-required-asterisk" aria-hidden="true">*</span>
                      </span>
                    </label>
                    <label className="stc-ooh-checkbox-label">
                      <input
                        type="checkbox"
                        checked={oohSignaturePresent}
                        onChange={(e) => setOohSignaturePresent(e.target.checked)}
                        aria-required={isOutOfHoursVisit}
                        aria-describedby="ooh-sig-desc"
                      />
                      <span id="ooh-sig-desc">
                        Is the patient signature (or patient representative) present? (Y/N) <span className="stc-required-asterisk" aria-hidden="true">*</span>
                      </span>
                    </label>
                    <label className="stc-ooh-checkbox-label">
                      <input
                        type="checkbox"
                        checked={oohGpConfirmed}
                        onChange={(e) => setOohGpConfirmed(e.target.checked)}
                        aria-required={isOutOfHoursVisit}
                        aria-describedby="ooh-gp-desc"
                      />
                      <span id="ooh-gp-desc">
                        Has the claiming GP confirmed Non-Routine Out-of-Hours consultation? (Y/N) <span className="stc-required-asterisk" aria-hidden="true">*</span>
                      </span>
                    </label>
                    <label className="stc-ooh-checkbox-label">
                      <input
                        type="checkbox"
                        checked={oohCoopRota}
                        onChange={(e) => setOohCoopRota(e.target.checked)}
                      />
                      <span>
                        Was this consultation conducted while you were working in a rota for the Out of Hours Co-operative
                      </span>
                    </label>
                  </div>
                </div>
              )}

            </>
          )}

          {formType === 'MICS' && (
            <>
              <div className="stc-form-grid two">
                <div>
                  <label htmlFor="motherName">
                    Mother&apos;s Name <span className="stc-muted">(required)</span>
                  </label>
                  <div className={`stc-typeahead-wrap${isStandalonePptMode ? ' stc-typeahead-wrap-with-spinner' : ''}`}>
                    <input
                      ref={motherNameInputRef}
                      id="motherName"
                      type="text"
                      value={motherName}
                      onChange={(e) => {
                        setMotherName(e.target.value)
                        setShowMicsNameSuggestions(true)
                      }}
                      onFocus={() => setShowMicsNameSuggestions(true)}
                      onBlur={handleMicsNameBlur}
                      onKeyDown={handleMicsNameKeyDown}
                      autoComplete="off"
                      aria-required="true"
                      aria-busy={isStandalonePptMode && motherNameQuery.length >= TYPEAHEAD_MIN_CHARS && isMicsNameSearching}
                    />
                    {isStandalonePptMode &&
                      motherNameQuery.length >= TYPEAHEAD_MIN_CHARS &&
                      isMicsNameSearching && (
                        <span className="stc-name-search-spinner-wrap" aria-live="polite" aria-label="Searching">
                          <svg className="gms-lookup-spinner stc-name-search-spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                            <circle cx="9" cy="9" r="7" strokeDasharray="44" strokeDashoffset="11" />
                          </svg>
                        </span>
                      )}
                    {isStandalonePptMode && showMicsNameSuggestions && micsNameSuggestions.length > 0 && (
                      <div className="stc-typeahead-list" role="listbox" aria-label="MICS patient name suggestions">
                        {micsNameSuggestions.map((s, idx) => (
                          <button
                            key={s.id}
                            type="button"
                            className={`stc-typeahead-item ${idx === activeMicsNameSuggestionIndex ? 'active' : ''}`}
                            onMouseDown={(e) => {
                              e.preventDefault()
                              handleSelectTypeaheadSuggestion(s)
                            }}
                          >
                            <span className="stc-typeahead-primary">{s.patientName || 'Unknown patient'}</span>
                            <span className="stc-typeahead-meta">
                              {s.gmsNumber ? `GMS ${s.gmsNumber}` : 'No GMS'}
                              {s.dateOfBirth || (s.formData?.dateOfBirth as string)
                                ? ` • DOB ${formatTypeaheadDob(s.dateOfBirth || (s.formData?.dateOfBirth as string))}`
                                : ''}
                              {s.ppsn || (s.formData?.ppsn as string)
                                ? ` • PPS ${(s.ppsn || (s.formData?.ppsn as string))}`
                                : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="micsPpsn">
                    PPS Number <span className="stc-muted">(required)</span>
                  </label>
                  <input
                    id="micsPpsn"
                    type="text"
                    value={ppsn}
                    onChange={(e) => setPpsn(e.target.value)}
                    placeholder="e.g. 1234567A"
                    aria-required="true"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="stc-form-grid two">
                <div>
                  <label htmlFor="micsClaimDate">
                    Claim Date <span className="stc-muted">(required)</span>
                  </label>
                  <input
                    id="micsClaimDate"
                    type="date"
                    value={micsClaimDate}
                    onChange={(e) => setMicsClaimDate(e.target.value)}
                    aria-required="true"
                  />
                </div>
                <div>
                  <label htmlFor="micsEDD">
                    Expected Delivery Date <span className="stc-muted">(required)</span>
                  </label>
                  <input
                    id="micsEDD"
                    type="date"
                    value={micsEDD}
                    onChange={(e) => setMicsEDD(e.target.value)}
                    aria-required="true"
                  />
                </div>
              </div>
              <div className="stc-form-grid two">
                <div>
                  <label htmlFor="micsService">
                    Service Provided <span className="stc-muted">(required)</span>
                  </label>
                  <input
                    id="micsService"
                    type="text"
                    value={micsService}
                    onChange={(e) => setMicsService(e.target.value)}
                    placeholder="e.g., Antenatal visit"
                    aria-required="true"
                  />
                </div>
              </div>
            </>
          )}

          <div className="stc-form-section">
            <p className="stc-declaration">{declarationText}</p>
            {!useStoredSignatureView && (
              <label className="stc-paper-sign-toggle">
                <input
                  type="checkbox"
                  checked={patientSignedOnPaper}
                  onChange={(e) => setPatientSignedOnPaper(e.target.checked)}
                />
                <span>Patient signed on paper (alternative to electronic signature)</span>
              </label>
            )}
            {patientSignedOnPaper ? (
              <div className="stc-paper-sign-notice" role="status">
                {useStoredSignatureView
                  ? 'Signed on paper. This claim was signed on paper (read-only).'
                  : 'Signed on paper. No electronic signature was captured for this claim.'}
              </div>
            ) : (
              <div className="stc-sig-wrap">
                <div className="stc-sig-toolbar">
                  <strong>Signature</strong>
                  {!useStoredSignatureView && (
                    <button type="button" className="stc-btn-danger" onClick={clearSignature}>
                      Clear Signature
                    </button>
                  )}
                  {!useStoredSignatureView && (
                    <button
                      type="button"
                      className="stc-btn"
                      onClick={handleWacomSign}
                      disabled={wacomCapturing}
                      aria-label="Capture signature using Wacom pad"
                    >
                      {wacomCapturing ? 'Opening pad…' : 'Click to Sign (Wacom)'}
                    </button>
                  )}
                  <span className="stc-muted" aria-live="polite">
                    {useStoredSignatureView
                      ? 'Previously captured signature (read-only)'
                      : (wacomAvailable ? 'Or draw in the box below' : 'Draw in the box below (Wacom needs licence setup)')}
                  </span>
                </div>
                <div className="stc-sig-canvas-wrap">
                  {useStoredSignatureView ? (
                    isStoredSignatureLoading ? (
                      <div className="stc-sig-readonly-state">Loading stored signature…</div>
                    ) : storedSignatureDataUrl ? (
                      <img className="stc-sig-image" src={storedSignatureDataUrl} alt="Previously captured signature" />
                    ) : (
                      <div className="stc-sig-readonly-state">
                        {storedSignatureError || 'No stored signature available for this record.'}
                      </div>
                    )
                  ) : (
                    <canvas
                      ref={canvasRef}
                      className="stc-sig-canvas"
                      width={600}
                      height={170}
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                      onPointerLeave={handlePointerUp}
                      style={{ touchAction: 'none' }}
                      aria-label="Signature pad"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="stc-form-actions">
            {saveSuccess && !embedded ? (
              <button type="button" className="stc-btn primary" onClick={clearForm}>
                Sign Another Form
              </button>
            ) : (
              <>
                {!embedded && (
                  <>
                    <button type="button" className="stc-btn ghost" onClick={clearForm}>
                      Clear Form
                    </button>
                    <button type="button" className="stc-btn ghost" onClick={handlePreview}>
                      Preview
                    </button>
                    <button type="button" className="stc-btn ghost" onClick={handleDownload}>
                      Download Form
                    </button>
                  </>
                )}
                {!isReadOnlyMode && (
                  <button type="button" className="stc-btn primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </>
            )}
          </div>
          </fieldset>

          {formError && (
            <p className="stc-error" role="alert">
              {formError}
            </p>
          )}
          {saveSuccess && (
            <div>
              <p className="stc-ok" role="status">
                STC record saved successfully.
              </p>
            </div>
          )}
          {saveWarning && (
            <p className="stc-warning" role="status">
              {saveWarning}
            </p>
          )}
        </form>

        <footer className="stc-signing-footer">
          Retention: Keep this record for at least 6 years for PCRS audit readiness.
        </footer>
      </div>
    </div>
  )
}
